import { usePermissions } from "hooks/use-permissions";
import { AppPermissions } from "models/permissions/permissions";
import { ReactNode } from "react";
import { Navigate, useLocation, useParams } from "react-router";

type ProtectedRouteProps = {
  children: ReactNode;
  redirectTo?: string;
  permission?: keyof AppPermissions;
  accessCondition?: (param?: string) => boolean;
};

export default function ProtectedRoute({
  children,
  redirectTo = "/",
  permission,
  accessCondition
}: ProtectedRouteProps) {
  const { inboxType, customerId } = useParams<{
    inboxType?: string;
    customerId?: string;
  }>();
  const location = useLocation();
  const permissions = usePermissions();

  const hasAccess = permission
    ? permissions[permission]
    : accessCondition
    ? accessCondition(inboxType)
    : true;

  if (!hasAccess) {
    // For external users redirecting from /inbox/all to /inbox/my:
    // Build absolute redirect path preserving full URL structure (e.g., /threads/123, query params)
    if (redirectTo === "my") {
      // Match `/inbox/all` as a distinct path segment (e.g., `/inbox/all`, `/inbox/all/123`, `/foo/inbox/all`)
      const inboxAllPattern = /(^|\/)inbox\/all(\/|$)/;
      if (inboxAllPattern.test(location.pathname)) {
        const redirectPath = location.pathname.replace(
          inboxAllPattern,
          (_match, prefix, suffix) => `${prefix}inbox/my${suffix}`
        ) + location.search;
        return <Navigate to={redirectPath} replace />;
      }
    }

    // PRIORITY 2: Customer-scoped relative navigation
    // If we're in a customer context and redirectTo is relative (no leading /),
    // construct absolute path to preserve customerId
    if (customerId && !redirectTo.startsWith("/")) {
      const customerScopedPath = `/customers/${customerId}/${redirectTo}`;
      return <Navigate to={customerScopedPath} replace />;
    }

    // PRIORITY 3: Default behavior - use redirectTo as-is
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
}
